import React, { useEffect, useState, useMemo } from 'react';
import {
    Modal,
    Tabs,
    Card,
    Row,
    Col,
    Tag,
    Badge,
    Typography,
    DatePicker,
    Table,
    Spin,
    Button,
    Statistic,
    Empty,
    Tooltip,
    Space,
    Progress
} from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ReloadOutlined,
    CompassOutlined,
    HeartFilled,
    SkinOutlined,
    HistoryOutlined,
    GiftOutlined,
    ThunderboltOutlined,
    SafetyCertificateOutlined,
    AimOutlined,
    FireOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
    GameLogEntry,
    UserGamePosition,
    UserOnlineGameStatus,
    UserStatsUnity,
    GetUserGameLogs,
    GetUserOnlineGameStatus,
    GetUserGamePosition,
    GetUserGameStatsUnity,
    GetUserInteractableRewardsList
} from '../../../dao/jutge/GetGameSessionsLogs';
import { GetUserEquipment, GetSkins, getJavamonImage } from '../../../dao/jutge/GetJavamonConfig';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

interface GameSessionModalProps {
    visible: boolean;
    onClose: () => void;
    student: {
        id: number;
        nom: string;
        cognoms: string;
        avatar?: string;
    } | null;
}

export const GameSessionModal: React.FC<GameSessionModalProps> = ({
    visible,
    onClose,
    student
}) => {
    const [loading, setLoading] = useState(false);
    const [logsLoading, setLogsLoading] = useState(false);

    // Online & Position status
    const [onlineStatus, setOnlineStatus] = useState<UserOnlineGameStatus>({ online: false });
    const [position, setPosition] = useState<UserGamePosition | null>(null);

    // Equipment & Stats
    const [statsUnity, setStatsUnity] = useState<UserStatsUnity | null>(null);
    const [equipmentIds, setEquipmentIds] = useState<any>(null);
    const [allCatalogSkins, setAllCatalogSkins] = useState<any>(null);

    // Logs & Date filter
    const [logs, setLogs] = useState<GameLogEntry[]>([]);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

    // Interactable rewards claimed
    const [interactables, setInteractables] = useState<any[]>([]);

    const fetchData = async () => {
        if (!student) return;
        setLoading(true);
        try {
            const [onlineRes, posRes, statsRes, eqRes, catRes, intRes] = await Promise.all([
                GetUserOnlineGameStatus(student.id),
                GetUserGamePosition(student.id),
                GetUserGameStatsUnity(student.id),
                GetUserEquipment(student.id),
                GetSkins(),
                GetUserInteractableRewardsList(student.id)
            ]);

            setOnlineStatus(onlineRes);
            setPosition(posRes);
            setStatsUnity(statsRes);
            setEquipmentIds(eqRes);
            setAllCatalogSkins(catRes);
            setInteractables(intRes || []);
        } catch (e) {
            console.error('Error fetching game session details:', e);
        } finally {
            setLoading(false);
        }
    };

    const fetchLogs = async (startStr?: string, endStr?: string) => {
        if (!student) return;
        setLogsLoading(true);
        try {
            const result = await GetUserGameLogs(student.id, startStr, endStr);
            setLogs(result || []);
        } catch (e) {
            console.error('Error fetching game logs:', e);
            setLogs([]);
        } finally {
            setLogsLoading(false);
        }
    };

    useEffect(() => {
        if (visible && student) {
            fetchData();
            fetchLogs();
        } else {
            setOnlineStatus({ online: false });
            setPosition(null);
            setStatsUnity(null);
            setEquipmentIds(null);
            setLogs([]);
            setDateRange(null);
        }
    }, [visible, student?.id]);

    const handleDateChange = (dates: any) => {
        setDateRange(dates);
        if (dates && dates[0] && dates[1]) {
            const startStr = dates[0].format('YYYY-MM-DD');
            const endStr = dates[1].format('YYYY-MM-DD');
            fetchLogs(startStr, endStr);
        } else {
            fetchLogs();
        }
    };

    // Calculate session duration metrics if logins/logouts are paired
    const processedLogs = useMemo(() => {
        return logs.map((log, index) => {
            const momentDate = dayjs(log.moment);
            let durationStr = '-';
            // If it's a LOGOUT or save, search for previous LOGIN in chronological order
            if (log.tipo === 'LOGOUT') {
                for (let i = index + 1; i < logs.length; i++) {
                    if (logs[i].tipo === 'LOGIN') {
                        const loginDate = dayjs(logs[i].moment);
                        const diffMins = momentDate.diff(loginDate, 'minute');
                        if (diffMins >= 0 && diffMins < 1440) {
                            const hours = Math.floor(diffMins / 60);
                            const mins = diffMins % 60;
                            durationStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
                        }
                        break;
                    }
                }
            }
            return {
                ...log,
                key: log.id,
                formattedDate: momentDate.format('DD/MM/YYYY HH:mm:ss'),
                duration: durationStr
            };
        });
    }, [logs]);

    const logColumns = [
        {
            title: 'Fecha y Hora',
            dataIndex: 'formattedDate',
            key: 'formattedDate',
            width: 170
        },
        {
            title: 'Tipo de Evento',
            dataIndex: 'tipo',
            key: 'tipo',
            width: 160,
            render: (tipo: string) => {
                let color = 'blue';
                let label = tipo;
                if (tipo === 'LOGIN') {
                    color = 'green';
                    label = '🟢 CONEXIÓN (Login)';
                } else if (tipo === 'LOGOUT') {
                    color = 'volcano';
                    label = '🔴 DESCONEXIÓN (Logout)';
                } else if (tipo === 'Battleground') {
                    color = 'purple';
                    label = '⚔️ Entró a Batalla';
                } else if (tipo === 'ReturnFromBattleground') {
                    color = 'cyan';
                    label = '🛡️ Regresó de Batalla';
                } else if (tipo === 'PeriodicSave') {
                    color = 'default';
                    label = '💾 Autoguardado';
                } else if (tipo && tipo.startsWith('JAVABALL:')) {
                    const parts = tipo.split(':');
                    const ballNom = parts[1] || 'JAVAball';
                    const jvNom = parts[2] || 'Javamon';
                    const res = parts[3] || '';
                    const isCap = res === 'CAPTURADO';
                    color = isCap ? 'green' : 'volcano';
                    label = `🎯 Lanzó ${ballNom} a ${jvNom} (${isCap ? 'CAPTURADO' : 'ESCAPADO'})`;
                }
                return <Tag color={color}>{label}</Tag>;
            }
        },
        {
            title: 'Duración Estimada',
            dataIndex: 'duration',
            key: 'duration',
            width: 130,
            render: (d: string) => (
                <Text type={d !== '-' ? 'success' : 'secondary'} strong={d !== '-'}>
                    {d}
                </Text>
            )
        }
    ];

    // Helper for finding item in catalog
    const findItemInCatalog = (type: string, id?: number) => {
        if (!allCatalogSkins || !id) return null;
        if (type === 'skin') return allCatalogSkins.skins?.find((s: any) => s.id === id);
        if (type === 'arma') return allCatalogSkins.armes?.find((a: any) => a.id === id);
        if (type === 'escut') return allCatalogSkins.escuts?.find((e: any) => e.id === id);
        if (type === 'arc') return allCatalogSkins.arcs?.find((a: any) => a.id === id);
        if (type === 'flecha') return allCatalogSkins.flechas?.find((f: any) => f.id === id);
        if (type === 'magia') return allCatalogSkins.magias?.find((m: any) => m.id === id);
        return null;
    };

    const skinItem = findItemInCatalog('skin', equipmentIds?.id_skin);
    const armaItem = findItemInCatalog('arma', equipmentIds?.id_arma);
    const escutItem = findItemInCatalog('escut', equipmentIds?.id_escut);
    const arcItem = findItemInCatalog('arc', equipmentIds?.id_arc);
    const flechaItem = findItemInCatalog('flecha', equipmentIds?.id_flecha);
    const magiaItem = findItemInCatalog('magia', equipmentIds?.id_magia);

    const renderEquipmentCard = (
        title: string,
        item: any,
        type: string,
        fallbackName?: string,
        statsList: { label: string; value: any; color?: string }[] = []
    ) => {
        const imgUrl = item
            ? getJavamonImage({ ...item, type })
            : (equipmentIds?.[`id_${type}`] ? `${process.env.REACT_APP_API_URL}:8061/jutge/game/image/${type}/${equipmentIds[`id_${type}`]}` : null);

        const name = item?.nom || fallbackName || 'Ninguno equipado';

        return (
            <Card
                size="small"
                style={{
                    borderRadius: 10,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                    border: '1px solid #e8e8e8',
                    height: '100%'
                }}
                bodyStyle={{ padding: 12, display: 'flex', flexDirection: 'column', height: '100%' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Text strong style={{ fontSize: 13, textTransform: 'uppercase', color: '#595959' }}>
                        {title}
                    </Text>
                    {item?.type && <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>{item.type}</Tag>}
                </div>

                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
                    <div
                        style={{
                            width: 58,
                            height: 58,
                            borderRadius: 8,
                            backgroundColor: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            border: '1px solid #d9d9d9',
                            flexShrink: 0
                        }}
                    >
                        {imgUrl ? (
                            <img
                                src={imgUrl}
                                alt={name}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                onError={(e) => {
                                    (e.target as HTMLElement).style.display = 'none';
                                }}
                            />
                        ) : (
                            <SkinOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />
                        )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ fontSize: 14, display: 'block', color: '#1f1f1f' }} ellipsis title={name}>
                            {name}
                        </Text>
                        {item?.ident && (
                            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                                Ref: {item.ident}
                            </Text>
                        )}
                    </div>
                </div>

                <div style={{ marginTop: 'auto', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {statsList.map((st, i) => (
                        st.value !== undefined && st.value !== null ? (
                            <Tag key={i} color={st.color || 'geekblue'} style={{ fontSize: 11, margin: 0 }}>
                                {st.label}: <strong>{st.value}</strong>
                            </Tag>
                        ) : null
                    ))}
                </div>
            </Card>
        );
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Title level={4} style={{ margin: 0 }}>
                        🎮 Registro de Sesión y Estado de Juego
                    </Title>
                    {student && (
                        <Tag color="purple" style={{ fontSize: 13, padding: '2px 8px', borderRadius: 6 }}>
                            {student.nom} {student.cognoms} (ID: {student.id})
                        </Tag>
                    )}
                </div>
            }
            open={visible}
            onCancel={onClose}
            width={950}
            footer={[
                <Button key="refresh" icon={<ReloadOutlined />} onClick={() => { fetchData(); fetchLogs(); }}>
                    Actualizar
                </Button>,
                <Button key="close" type="primary" onClick={onClose}>
                    Cerrar
                </Button>
            ]}
        >
            <Spin spinning={loading}>
                {/* STATUS BAR: ONLINE, SCENE, HEALTH, COORDS */}
                <Card
                    style={{
                        marginBottom: 16,
                        borderRadius: 12,
                        background: 'linear-gradient(135deg, #f6f8fb 0%, #e9edf4 100%)',
                        border: '1px solid #dbe2ec'
                    }}
                    bodyStyle={{ padding: '16px 20px' }}
                >
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={6}>
                            <Statistic
                                title={<Text strong style={{ color: '#595959' }}>Estado en Unity</Text>}
                                value={onlineStatus.online ? 'CONECTADO' : 'DESCONECTADO'}
                                valueStyle={{
                                    color: onlineStatus.online ? '#52c41a' : '#8c8c8c',
                                    fontWeight: 'bold',
                                    fontSize: 18
                                }}
                                prefix={
                                    onlineStatus.online ? (
                                        <Badge status="processing" color="#52c41a" style={{ marginRight: 6 }} />
                                    ) : (
                                        <Badge status="default" style={{ marginRight: 6 }} />
                                    )
                                }
                            />
                            {onlineStatus.online && (
                                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                                    Sala / Room: {onlineStatus.roomId || 'General'}
                                </Text>
                            )}
                        </Col>

                        <Col xs={12} sm={6}>
                            <Statistic
                                title={<Text strong style={{ color: '#595959' }}><CompassOutlined /> Pantalla / Escena</Text>}
                                value={position?.scene || 'Pueblo 0'}
                                valueStyle={{ fontSize: 17, fontWeight: 600, color: '#1890ff' }}
                            />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                Posición: X: {position?.pos_x?.toFixed(1) ?? 0}, Y: {position?.pos_y?.toFixed(1) ?? 0}
                            </Text>
                        </Col>

                        <Col xs={12} sm={6}>
                            <Statistic
                                title={<Text strong style={{ color: '#595959' }}><HeartFilled style={{ color: '#ff4d4f' }} /> Vidas / Salud</Text>}
                                value={position?.salut !== null && position?.salut !== undefined ? position.salut : (statsUnity?.salut ?? 100)}
                                suffix={`/ ${statsUnity?.salut ?? 100}`}
                                valueStyle={{
                                    color: (position?.salut ?? 100) > 30 ? '#ff4d4f' : '#cf1322',
                                    fontWeight: 'bold',
                                    fontSize: 18
                                }}
                            />
                            <Progress
                                percent={Math.min(100, Math.round(((position?.salut ?? (statsUnity?.salut || 100)) / (statsUnity?.salut || 100)) * 100))}
                                strokeColor="#ff4d4f"
                                size="small"
                                showInfo={false}
                                style={{ marginTop: 2 }}
                            />
                        </Col>

                        <Col xs={24} sm={6}>
                            <Statistic
                                title={<Text strong style={{ color: '#595959' }}><GiftOutlined /> Cofres Reclamados</Text>}
                                value={interactables.length}
                                valueStyle={{ fontSize: 18, fontWeight: 'bold', color: '#722ed1' }}
                            />
                            {position?.id_actividad && (
                                <Tag color="gold" style={{ fontSize: 11, marginTop: 4 }}>
                                    Actividad actual: #{position.id_actividad}
                                </Tag>
                            )}
                        </Col>
                    </Row>
                </Card>

                {/* TABS: EQUIPAMIENTO & HISTORIAL DE SESIONES */}
                <Tabs defaultActiveKey="equipment" type="card">
                    <TabPane
                        tab={
                            <span>
                                <SkinOutlined />
                                Equipamiento Actual ({statsUnity?.pj || 'Personaje'})
                            </span>
                        }
                        key="equipment"
                    >
                        <Row gutter={[12, 12]}>
                            {/* 1. SKIN */}
                            <Col xs={24} sm={12} md={8}>
                                {renderEquipmentCard(
                                    'Skin / Personaje',
                                    skinItem,
                                    'skin',
                                    statsUnity?.pj,
                                    [
                                        { label: 'Defensa', value: statsUnity?.skinDefensa ?? skinItem?.defensa, color: 'blue' },
                                        { label: 'Salud Base', value: statsUnity?.salut ?? skinItem?.salut, color: 'red' },
                                        { label: 'Velocidad', value: statsUnity?.velocitat ?? skinItem?.velocitat, color: 'green' }
                                    ]
                                )}
                            </Col>

                            {/* 2. ARMA */}
                            <Col xs={24} sm={12} md={8}>
                                {renderEquipmentCard(
                                    'Arma Principal',
                                    armaItem,
                                    'arma',
                                    statsUnity?.arma,
                                    [
                                        { label: 'Daño', value: statsUnity?.armaDany ?? armaItem?.dany, color: 'magenta' },
                                        { label: 'Crítico', value: statsUnity?.armaCritico ?? armaItem?.critico ? `${statsUnity?.armaCritico ?? armaItem?.critico}%` : null, color: 'orange' },
                                        { label: 'Velocidad', value: statsUnity?.armaVelocidad ?? armaItem?.velocidad, color: 'green' }
                                    ]
                                )}
                            </Col>

                            {/* 3. ESCUDO */}
                            <Col xs={24} sm={12} md={8}>
                                {renderEquipmentCard(
                                    'Escudo',
                                    escutItem,
                                    'escut',
                                    statsUnity?.escut,
                                    [
                                        { label: 'Defensa', value: statsUnity?.escutDefensa ?? escutItem?.defensa, color: 'blue' },
                                        { label: 'Bloqueo', value: statsUnity?.escutBloqueo ?? escutItem?.bloqueo, color: 'cyan' },
                                        { label: 'Regeneración', value: statsUnity?.escutRegeneracion ?? escutItem?.regeneracion, color: 'green' }
                                    ]
                                )}
                            </Col>

                            {/* 4. ARCO */}
                            <Col xs={24} sm={12} md={8}>
                                {renderEquipmentCard(
                                    'Arco',
                                    arcItem,
                                    'arc',
                                    statsUnity?.arc,
                                    [
                                        { label: 'Daño', value: statsUnity?.arcDany ?? arcItem?.dany, color: 'magenta' },
                                        { label: 'Rango', value: statsUnity?.arcRang ?? arcItem?.rang, color: 'purple' },
                                        { label: 'Velocidad', value: statsUnity?.arcVelocidad ?? arcItem?.velocidad, color: 'green' }
                                    ]
                                )}
                            </Col>

                            {/* 5. FLECHAS */}
                            <Col xs={24} sm={12} md={8}>
                                {renderEquipmentCard(
                                    'Flechas',
                                    flechaItem,
                                    'flecha',
                                    statsUnity?.flecha,
                                    [
                                        { label: 'Daño extra', value: statsUnity?.flechaDany ?? flechaItem?.dany, color: 'magenta' },
                                        { label: 'Crítico extra', value: statsUnity?.flechaCritico ?? flechaItem?.critico ? `${statsUnity?.flechaCritico ?? flechaItem?.critico}%` : null, color: 'orange' }
                                    ]
                                )}
                            </Col>

                            {/* 6. MAGIA / HABILIDAD */}
                            <Col xs={24} sm={12} md={8}>
                                {renderEquipmentCard(
                                    'Magia / Hechizo',
                                    magiaItem,
                                    'magia',
                                    statsUnity?.magia,
                                    [
                                        { label: 'Daño', value: statsUnity?.magiaDany ?? magiaItem?.dany, color: 'magenta' },
                                        { label: 'Rango', value: statsUnity?.magiaRang ?? magiaItem?.rang, color: 'purple' },
                                        { label: 'Elemento', value: statsUnity?.magiaElemento ?? magiaItem?.elemento, color: 'gold' }
                                    ]
                                )}
                            </Col>
                        </Row>
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <HistoryOutlined />
                                Historial de Conexiones ({logs.length})
                            </span>
                        }
                        key="logs"
                    >
                        <div style={{ marginBottom: 14, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Text strong>Filtrar por rango de fechas:</Text>
                            <RangePicker
                                value={dateRange as any}
                                onChange={handleDateChange}
                                format="YYYY-MM-DD"
                                allowClear
                            />
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => handleDateChange(null)}
                            >
                                Limpiar Filtro
                            </Button>
                            <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>
                                Total eventos encontrados: {logs.length}
                            </Text>
                        </div>

                        <Table
                            dataSource={processedLogs}
                            columns={logColumns}
                            rowKey="id"
                            loading={logsLoading}
                            pagination={{ pageSize: 8, showSizeChanger: true }}
                            size="small"
                        />
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <GiftOutlined />
                                Interacciones y Recompensas ({interactables.length})
                            </span>
                        }
                        key="interactables"
                    >
                        {interactables.length === 0 ? (
                            <Empty description="No se han registrado cofres ni interactables reclamados por este alumno aún." />
                        ) : (
                            <Row gutter={[10, 10]}>
                                {interactables.map((item, idx) => (
                                    <Col xs={24} sm={12} md={8} key={idx}>
                                        <Card size="small" style={{ borderRadius: 8 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Text strong>{item.interactable_id || item.nom || `Cofre #${idx + 1}`}</Text>
                                                <Tag color="green">Reclamado</Tag>
                                            </div>
                                            {item.moment && (
                                                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                                                    {dayjs(item.moment).format('DD/MM/YYYY HH:mm:ss')}
                                                </Text>
                                            )}
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </TabPane>
                </Tabs>
            </Spin>
        </Modal>
    );
};

export default GameSessionModal;
